import { Env } from "~/utils/Env";

type AuthProvider = "zitadel" | "workos";

export const AUTHKIT_URL: string = Env.VITE_AUTHKIT_URL ?? "";

/**
 * Per-browser override keys, each only able to CONTRADICT the deploy's env
 * default (a key agreeing with the default is a no-op). Both are read ONCE
 * at module load — set/remove the flag, then reload.
 *
 * On a Zitadel-default deploy, force the WorkOS path for THIS browser
 * (tester upgrade; requires authkit-ui configured via `VITE_AUTHKIT_URL`):
 *
 *   localStorage.setItem("meq:forceWorkosAuth", "1");  // then reload
 *   localStorage.removeItem("meq:forceWorkosAuth");     // back to the default
 *
 * On a WorkOS-default deploy (staging during the bake, prod after the V8
 * cutover), revert THIS browser to Zitadel — the escape hatch while both
 * auth paths exist; deleted with the Zitadel path in V9:
 *
 *   localStorage.setItem("meq:forceZitadelAuth", "1");  // then reload
 *   localStorage.removeItem("meq:forceZitadelAuth");     // back to the default
 */
const FORCE_WORKOS_KEY = "meq:forceWorkosAuth";
const FORCE_ZITADEL_KEY = "meq:forceZitadelAuth";

function hasOverrideFlag(key: string): boolean {
  try {
    return (
      typeof window !== "undefined" && window.localStorage.getItem(key) === "1"
    );
  } catch {
    // localStorage can throw (private mode / disabled / SSR) — treat as unset.
    return false;
  }
}

const envAuthProvider: AuthProvider =
  Env.VITE_AUTH_PROVIDER === "workos" ? "workos" : "zitadel";

export const AUTH_PROVIDER: AuthProvider =
  envAuthProvider === "workos"
    ? hasOverrideFlag(FORCE_ZITADEL_KEY)
      ? "zitadel"
      : "workos"
    : AUTHKIT_URL && hasOverrideFlag(FORCE_WORKOS_KEY)
      ? "workos"
      : "zitadel";

export const isWorkOSAuth = (): boolean => AUTH_PROVIDER === "workos";

/** Canonical login redirect to authkit-ui. The language rides the shared
 * preference cookie (see lib/preferenceCookie), not the URL. */
// Customer subdomains forwarded to authkit-ui as `?brand=` — authkit is one
// shared host and cannot derive them itself. Drives the post-auth redirect
// back to the subdomain.
const BRANDED_SUBDOMAINS = ["alliance", "gmp", "jbs"];

function brandedSubdomain(): string | undefined {
  const subdomain = window.location.hostname?.split(".")[0] ?? "";
  return BRANDED_SUBDOMAINS.includes(subdomain) ? subdomain : undefined;
}

export function buildAuthkitLoginUrl(options?: {
  returnTo?: string;
  authkitUrl?: string;
}): string {
  const url = new URL(`${options?.authkitUrl ?? AUTHKIT_URL}/`);
  if (options?.returnTo) {
    url.searchParams.set("returnTo", options.returnTo);
  }
  const brand = brandedSubdomain();
  if (brand) {
    url.searchParams.set("brand", brand);
  }
  return url.toString();
}

export function redirectToAuthkitLogin(options?: { returnTo?: string }): void {
  window.location.assign(buildAuthkitLoginUrl(options));
}

export function buildAuthkitLogoutUrl(): string {
  const url = new URL(`${AUTHKIT_URL}/logout`);
  const brand = brandedSubdomain();
  if (brand) {
    url.searchParams.set("brand", brand);
  }
  return url.toString();
}

export function redirectToAuthkitLogout(): void {
  window.location.assign(buildAuthkitLogoutUrl());
}

const AUTO_REDIRECT_GUARD_KEY = "meq:authkitAutoRedirects";
const AUTO_REDIRECT_MAX = 2;
const AUTO_REDIRECT_WINDOW_MS = 30_000;

/**
 * Auto-redirect an unauthenticated visit to authkit-ui — unless this tab is
 * bouncing (more than AUTO_REDIRECT_MAX auto-redirects inside the window),
 * in which case it returns false and the caller renders the manual
 * splash-with-button fallback instead of looping.
 */
export function autoRedirectToAuthkitLogin(options?: {
  returnTo?: string;
}): boolean {
  let recentRedirects: number[] = [];
  try {
    const stored = window.sessionStorage.getItem(AUTO_REDIRECT_GUARD_KEY);
    recentRedirects = (JSON.parse(stored ?? "[]") as number[]).filter(
      (timestamp) => Date.now() - timestamp < AUTO_REDIRECT_WINDOW_MS,
    );
  } catch {
    recentRedirects = [];
  }

  if (recentRedirects.length >= AUTO_REDIRECT_MAX) {
    return false;
  }

  try {
    window.sessionStorage.setItem(
      AUTO_REDIRECT_GUARD_KEY,
      JSON.stringify([...recentRedirects, Date.now()]),
    );
  } catch {
    // sessionStorage unavailable — redirect anyway; the guard just won't
    // remember this hop.
  }

  redirectToAuthkitLogin(options);
  return true;
}
